/**
 * One crew member: their standing, their required documents, and the way back
 * on or off site.
 *
 * The design puts a Scan button beside every unmet requirement. Here that is an
 * Upload button, and pressing it swaps the document list for the upload form in
 * place rather than opening a second layer — a drawer over a drawer is where
 * the mockup's camera modal went, and without a camera there is nothing to
 * justify it.
 */
import {
  AlertTriangle,
  CheckCircle2,
  Paperclip,
  Phone,
  Upload,
} from "lucide-react";
import { useEffect, useState } from "react";
import { downloadAuthedFile } from "@/components/gb/authed-file";
import { crewDocumentDownloadPath } from "@gb/contracts";
import type { CrewDocStatus, CrewMember } from "@gb/contracts";
import { CrewAvatar, toneFor } from "@/components/gb/crew-avatar";
import { SectionLabel } from "@/components/gb/section-label";
import { StatusPill } from "@/components/gb/status-pill";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
} from "@/components/ui/sheet";
import { formatDate, formatDateShort } from "@/lib/format";
import { cn } from "@/lib/utils";
import { CertificateUpload } from "./certificate-upload";
import {
  useConfirmCertificate,
  useDeleteCertificate,
  useOffboard,
  useReonboard,
} from "./crew-api";

export function PersonDrawer({
  person,
  projectId,
  issuingBodies,
  startUploading = false,
  open,
  onClose,
}: {
  person: CrewMember | null;
  projectId: string;
  /** Doc type → the body that normally issues it, from the jurisdiction. */
  issuingBodies: Readonly<Record<string, string>>;
  /** Open straight onto the first outstanding document — "Add & upload cards". */
  startUploading?: boolean;
  open: boolean;
  onClose: () => void;
}) {
  const [chosen, setChosen] = useState<CrewDocStatus | null>(null);
  /** The user backed out of the auto-opened form; do not put it back. */
  const [dismissedAutoStart, setDismissedAutoStart] = useState(false);
  const confirmCertificate = useConfirmCertificate(projectId);
  const deleteCertificate = useDeleteCertificate(projectId);
  const offboard = useOffboard(projectId);
  const reonboard = useReonboard(projectId);
  const [error, setError] = useState<string | null>(null);

  // Reopening on a different person must not land on the previous one's upload
  // form, and closing mid-upload must not leave it there for next time.
  useEffect(() => {
    setChosen(null);
    setDismissedAutoStart(false);
    setError(null);
  }, [person?.id, open]);

  // Derived, not synced into state by an effect: "which document am I
  // uploading" is a function of what the user picked and how the drawer was
  // opened, and an effect mirroring that into state only adds a render where
  // the two disagree.
  const firstOutstanding =
    person?.perDoc.find((d) => d.status !== "valid") ?? null;
  const uploading =
    chosen ?? (startUploading && !dismissedAutoStart ? firstOutstanding : null);

  const stopUploading = (): void => {
    setChosen(null);
    setDismissedAutoStart(true);
  };

  if (!person) return null;

  const busy = offboard.isPending || reonboard.isPending;

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent className="flex w-[480px] max-w-full flex-col">
        <SheetHeader>{person.name}</SheetHeader>

        <SheetBody>
          {uploading ? (
            <CertificateUpload
              personId={person.id}
              personName={person.name}
              type={uploading.type}
              label={uploading.label}
              suggestedBody={issuingBodies[uploading.type]}
              onCancel={stopUploading}
              onSaved={async (input) => {
                await confirmCertificate.mutateAsync({
                  personId: person.id,
                  documentId: input.documentId,
                  input: {
                    issuingBody: input.issuingBody,
                    reference: input.reference,
                    issuedDate: input.issuedDate,
                    expiryDate: input.expiryDate,
                  },
                });
                stopUploading();
              }}
            />
          ) : (
            <>
              <div className="mb-[18px] flex items-center gap-3.5">
                <CrewAvatar
                  initials={person.initials}
                  tone={toneFor(person.overall)}
                  size={54}
                />
                <div className="min-w-0 flex-1">
                  <div className="font-display text-[1.3rem] font-bold uppercase leading-[1.05] tracking-[0.01em] text-ink">
                    {person.name}
                  </div>
                  <div className="mt-[3px] text-[0.84rem] text-muted">
                    {person.roleLabel} ·{" "}
                    {person.offSite
                      ? "off site"
                      : `on site since ${formatDateShort(person.startDate)}`}
                  </div>
                  {person.contact !== null && (
                    <a
                      href={`tel:${person.contact.replace(/\s+/g, "")}`}
                      className="mt-[7px] flex items-center gap-[7px] text-[0.88rem] font-semibold text-ink hover:underline"
                    >
                      <span className="flex text-brand">
                        <Phone size={15} aria-hidden />
                      </span>
                      {person.contact}
                    </a>
                  )}
                </div>
              </div>

              <StandingBanner person={person} />

              {person.perDoc.length > 0 ? (
                <>
                  <SectionLabel className="mt-[22px] mb-1 px-0.5">
                    Required documents
                  </SectionLabel>
                  <div>
                    {person.perDoc.map((doc) => (
                      <DocRow
                        key={doc.type}
                        doc={doc}
                        personId={person.id}
                        onUpload={() => setChosen(doc)}
                        onDiscard={
                          doc.pending
                            ? () =>
                                void deleteCertificate.mutateAsync({
                                  personId: person.id,
                                  documentId: doc.pending!.id,
                                })
                            : undefined
                        }
                      />
                    ))}
                  </div>
                </>
              ) : (
                <p className="mt-[22px] text-[0.86rem] leading-[1.55] text-muted">
                  No compliance profile is configured for this project&rsquo;s
                  country, so nothing is required of this role yet. Set one up
                  in the project setup wizard.
                </p>
              )}

              {error !== null && (
                <p
                  role="alert"
                  className="mt-4 text-[0.84rem] font-semibold text-danger"
                >
                  {error}
                </p>
              )}
            </>
          )}
        </SheetBody>

        {!uploading && (
          <SheetFooter>
            <Button
              variant="secondary"
              size="footer"
              disabled={busy}
              onClick={() => {
                setError(null);
                const action = person.offSite ? reonboard : offboard;
                action.mutate(person.id, {
                  onError: (caught: unknown) =>
                    setError(
                      caught instanceof Error
                        ? caught.message
                        : "That didn’t work.",
                    ),
                });
              }}
            >
              {person.offSite ? "Mark back on site" : "Mark off site"}
            </Button>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}

function StandingBanner({ person }: { person: CrewMember }) {
  const cleared = person.cleared;
  const amber = person.overall === "expiring";

  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-card border-[1.5px] px-4 py-3.5",
        cleared
          ? "border-ok/40 bg-ok-bg"
          : amber
            ? "border-warn/40 bg-warn-bg"
            : "border-danger/40 bg-danger-bg",
      )}
    >
      <span
        className={cn(
          "mt-0.5 flex shrink-0",
          cleared ? "text-ok" : amber ? "text-warn" : "text-danger",
        )}
      >
        {cleared ? (
          <CheckCircle2 size={18} aria-hidden />
        ) : (
          <AlertTriangle size={18} aria-hidden />
        )}
      </span>
      <div className="min-w-0">
        <div className="font-bold text-ink">
          {cleared
            ? "Cleared to work"
            : amber
              ? "Action needed soon"
              : person.overall === "expired"
                ? "Not cleared — document expired"
                : "Not cleared — document missing"}
        </div>
        <div className="text-[0.84rem] text-muted">
          {cleared
            ? "All required documents valid"
            : "Upload the flagged document to clear"}
        </div>
      </div>
    </div>
  );
}

function DocRow({
  doc,
  personId,
  onUpload,
  onDiscard,
}: {
  doc: CrewDocStatus;
  personId: string;
  onUpload: () => void;
  onDiscard?: () => void;
}) {
  // Either the confirmed certificate or an upload still waiting to be checked —
  // both have a scan behind them, and both are things the reader may want to
  // look at. `hasFile` is false for every seeded certificate, which is why this
  // is conditional rather than assumed.
  const file = doc.certificate ?? doc.pending;
  const [failed, setFailed] = useState(false);

  return (
    <div className="flex items-center gap-3 border-t-[1.5px] border-border py-3">
      <div className="min-w-0 flex-1">
        <div className="text-[0.92rem] font-bold text-ink">{doc.label}</div>
        <div className="text-[0.82rem] text-muted">{detailOf(doc)}</div>
        {file?.hasFile && file.filename && (
          // **What is actually on file.** The row used to say only "valid to 16
          // Jan 2029" — true, and no help to somebody asking *which document*
          // was accepted, or wanting to check the scan against the fields
          // somebody typed off it.
          <button
            type="button"
            onClick={() => {
              setFailed(false);
              void downloadAuthedFile(
                crewDocumentDownloadPath(personId, file.id),
                file.filename ?? "certificate",
              ).catch(() => setFailed(true));
            }}
            className="mt-1 flex min-w-0 cursor-pointer items-center gap-1.5 text-[0.8rem] font-semibold text-brand underline-offset-2 hover:underline"
          >
            <Paperclip size={12} aria-hidden className="shrink-0" />
            <span className="truncate">{file.filename}</span>
          </button>
        )}
        {failed && (
          <p
            role="alert"
            className="mt-1 text-[0.78rem] font-semibold text-danger"
          >
            That file couldn’t be opened.
          </p>
        )}
      </div>
      <StatusPill status={doc.status} />
      {doc.status !== "valid" && (
        <Button size="sm" onClick={onUpload} className="shrink-0">
          <Upload size={14} />
          Upload
        </Button>
      )}
      {doc.pending && onDiscard && (
        <button
          type="button"
          onClick={onDiscard}
          className="shrink-0 cursor-pointer text-[0.78rem] font-semibold text-muted underline-offset-2 hover:text-danger hover:underline"
        >
          Discard
        </button>
      )}
    </div>
  );
}

/** The line under a document's name: what we hold, or what we are waiting for. */
function detailOf(doc: CrewDocStatus): string {
  if (doc.status === "missing") {
    // A scan that is uploaded but unconfirmed is *not* on file in the sense
    // that matters, and saying so plainly is the point of the distinction.
    return doc.pending ? "Uploaded — needs checking" : "Not on file";
  }
  const body = doc.issuingBody ? `${doc.issuingBody} · ` : "";
  if (doc.status === "expired") {
    return `${body}expired ${Math.abs(doc.days ?? 0)}d ago`;
  }
  if (doc.status === "expiring") {
    return `${body}expires in ${doc.days ?? 0}d`;
  }
  return `${body}valid to ${doc.expiryDate ? formatDate(doc.expiryDate) : "—"}`;
}
